import { Component, type ErrorInfo, type JSX, type ReactNode } from 'react';

import { ButtonLink } from '@/components/ui/Button';
import { reportError } from '@/lib/errorReporting';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Top-level safety net for render errors outside the router (for example in the
 * providers themselves). Route-level failures are handled by each route's
 * `errorElement`, which keeps the shell mounted.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    reportError(error, { componentStack: errorInfo.componentStack ?? undefined });
  }

  override render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    return <FullPageError />;
  }
}

export function FullPageError(): JSX.Element {
  return (
    <div className="flex min-h-dvh items-center justify-center px-6 py-24">
      <div className="max-w-lg text-center">
        <p className="font-mono text-xs uppercase tracking-[0.18em] text-accent">Error</p>
        <h1 className="mt-3 text-3xl">Something went wrong</h1>
        <p className="mt-4 text-muted">
          The page failed to load. Reloading usually fixes it. If it keeps happening, the contact
          details in the footer still work.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex min-h-11 items-center justify-center rounded-md bg-accent-solid px-5 py-2.5 text-sm font-medium text-accent-contrast transition-colors duration-150 hover:bg-accent-solid-hover"
          >
            Reload the page
          </button>
          <ButtonLink to="/" variant="secondary">
            Go to the home page
          </ButtonLink>
        </div>
      </div>
    </div>
  );
}
